import copy from 'copy-to-clipboard';
import { memo, useCallback, useMemo, useState } from 'react';
import { playCheckboxSound, playDropdownClickSound } from '@wg/web2clientapi/sound';
import {
  Checkbox,
  ClanMember,
  ClanRole,
  ContextMenu,
  DivTooltip,
  TableBodyCell,
  TableRow,
  TooltipBody,
} from '@wg/wows-react-uikit';

import settings from '~/settings';
import { isInGame } from '~/constants';
import { isChatDenied as isChatDeniedHelper } from '~/helpers/account';
import { getPositionY } from '~/helpers/contextMenu';
import { floats, percent, thousands } from '~/helpers/formatting';
import { t } from '~/helpers/localization';
import { getRank, hasPermission, PERMISSIONS, getLocalizedName } from '~/roles';
import { LastBattleTimeTooltipContent, MemberIsBannedTooltip } from '~/tooltips';

import { FormattedLastBattleTime, ResourceCell, StatisticsValue } from '~/UIKit';

import type { IProps as IContextMenuProps } from '@wg/wows-react-uikit/components/ContextMenu/ContextMenu';
import type { TableBodyCellModifier } from '@wg/wows-react-uikit/components/Table/TableBodyCell';
import type { ICurrentAccountState } from '~/store/slices/currentAccountSlice';
import type { IClanMember } from '~/types/declaration';

const CLAN_BATTLES_TYPE = 'cvc';

interface MembersTableItemProps {
  clanId: number;
  currentAccount: ICurrentAccountState;
  member: IClanMember;

  isContextMenuEnabled?: boolean;
  displayCheckboxes?: boolean;
  disabledCheckbox?: boolean;
  isMember?: boolean;
  isSelectedMember?: boolean;
  currentBattleType?: string;

  openAccountProfile: (accountId: string) => void;
  openChatWindow: (accountId: string, nickname: string) => void;

  onTickMember?: (memberId: number) => void;
  onChangeCommanderClick?: (memberToDelegate?: IClanMember) => void;
  onChangeRoleClick?: (memberToChangeId?: number) => void;
  onRemoveMembersClick?: (members?: IClanMember[]) => void;
}

const MembersTableItem = ({
  clanId,
  currentAccount,
  member,

  isContextMenuEnabled = true,
  displayCheckboxes = false,
  disabledCheckbox = false,
  isMember = false,
  isSelectedMember = false,
  currentBattleType,

  openAccountProfile,
  openChatWindow,

  onTickMember,
  onChangeCommanderClick,
  onChangeRoleClick,
  onRemoveMembersClick,
}: MembersTableItemProps) => {
  const [isContextMenuActive, setIsContextMenuActive] = useState(false);
  const [contextMenuContext, setContextMenuContext] = useState<IContextMenuProps['context']>([]);
  const [contextMenuPosition, setContextMenuPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  const isOwn = currentAccount.id === member.id;
  const isOwnClan = currentAccount.clanId === clanId;
  const isBanned = Boolean(member.isBanned);
  const isHiddenStatistics = Boolean(member.isHiddenStatistics);
  const hideStatistics = !isOwn && isHiddenStatistics;

  const isChatDenied = useCallback(() => {
    return isChatDeniedHelper(currentAccount, member);
  }, [currentAccount, member]);

  const onContextMenuStateChange = useCallback((active: boolean) => {
    setIsContextMenuActive(active);
  }, []);

  const getContextMenuItemsContextForOwnClanMember = useCallback((): IContextMenuProps['context'][number] | null => {
    const accountRole = currentAccount.roleName;

    if (!accountRole || getRank(accountRole) <= getRank(member.roleName)) {
      return null;
    }

    const items: IContextMenuProps['context'][number]['items'] = [];

    if (onChangeRoleClick && hasPermission(accountRole, PERMISSIONS.CHANGE_ROLE)) {
      items.push({ name: t('Изменить должность'), value: 'CHANGE_ROLE', disabled: false });
    }

    if (onChangeCommanderClick && !isBanned && hasPermission(accountRole, PERMISSIONS.CHANGE_COMMANDER)) {
      items.push({ name: t('Передать управление'), value: 'CHANGE_COMMANDER', disabled: false });
    }

    if (onRemoveMembersClick && hasPermission(accountRole, PERMISSIONS.REMOVE_MEMBER)) {
      items.push({ name: t('Исключить из клана'), value: 'REMOVE_MEMBER', disabled: false });
    }

    if (!items.length) {
      return null;
    }

    return {
      isNeedDivider: true,
      items,
    };
  }, [
    currentAccount.roleName,
    isBanned,
    member.roleName,
    onChangeCommanderClick,
    onChangeRoleClick,
    onRemoveMembersClick,
  ]);

  const buildContextMenuContext = useCallback((): IContextMenuProps['context'] => {
    const banMsg = isBanned ? t('Аккаунт игрока заблокирован') : undefined;

    const baseItems: IContextMenuProps['context'][number]['items'] = [];

    if (!isOwn && isInGame) {
      baseItems.push({
        name: t('Отправить сообщение'),
        value: 'OPEN_CHAT_WINDOW',
        disabled: isBanned || isChatDenied(),
        tooltip: banMsg,
      });
    }

    baseItems.push(
      {
        name: t('Профиль игрока'),
        value: 'OPEN_ACCOUNT_PROFILE',
        disabled: isBanned,
        tooltip: banMsg,
      },
      {
        name: t('Копировать никнейм'),
        value: 'COPY_NICKNAME',
        disabled: false,
        tooltip: undefined,
      },
    );

    const context: IContextMenuProps['context'] = [
      {
        isNeedDivider: false,
        items: baseItems,
      },
    ];

    const isClanMember = currentAccount.clanId === clanId;
    if (isClanMember) {
      const group = getContextMenuItemsContextForOwnClanMember();
      if (group) {
        context.push(group);
      }
    }

    return context;
  }, [clanId, currentAccount.clanId, getContextMenuItemsContextForOwnClanMember, isBanned, isChatDenied, isOwn]);

  const onContextMenu = useCallback<React.MouseEventHandler<HTMLElement>>(
    (event) => {
      const context = buildContextMenuContext();
      const clientY = getPositionY(event, context);

      setContextMenuContext(context);
      setContextMenuPosition({
        x: event.clientX,
        y: clientY,
      });
    },
    [buildContextMenuContext],
  );

  const onContextMenuItemClick = useCallback<IContextMenuProps['onItemClick']>(
    (action) => {
      switch (action) {
        case 'CHANGE_COMMANDER':
          onChangeCommanderClick?.(member);
          return;

        case 'REMOVE_MEMBER':
          onRemoveMembersClick?.([member]);
          return;

        case 'CHANGE_ROLE':
          onChangeRoleClick?.(member.id);
          return;

        case 'OPEN_ACCOUNT_PROFILE':
          openAccountProfile(String(member.id));
          return;

        case 'OPEN_CHAT_WINDOW':
          if (!isChatDenied()) {
            void playDropdownClickSound();
            openChatWindow(String(member.id), member.name);
          }
          return;

        case 'COPY_NICKNAME':
          copy(member.name);
          return;
      }
    },
    [
      isChatDenied,
      member,
      onChangeCommanderClick,
      onChangeRoleClick,
      onRemoveMembersClick,
      openAccountProfile,
      openChatWindow,
    ],
  );

  const onTickMemberLocal = useCallback(() => {
    onTickMember?.(member.id);
  }, [member.id, onTickMember]);

  const onTableRowClick = useCallback(() => {
    if (isOwn || isContextMenuActive) {
      return;
    }

    const canQuickChat = (!displayCheckboxes || disabledCheckbox) && !isChatDenied();

    if (canQuickChat) {
      void playDropdownClickSound();
      openChatWindow(String(member.id), member.name);
      return;
    }

    onTickMemberLocal();
    void playCheckboxSound();
  }, [
    disabledCheckbox,
    displayCheckboxes,
    isChatDenied,
    isContextMenuActive,
    isOwn,
    member.id,
    member.name,
    onTickMemberLocal,
    openChatWindow,
  ]);

  const tableCellWithCheckboxes = useMemo(() => {
    if (!displayCheckboxes) {
      return null;
    }

    const tooltipContent = disabledCheckbox
      ? t('Вы не можете совершать действий с игроками равными по должности или выше.')
      : null;

    return (
      <TableBodyCell modify="check">
        <DivTooltip tooltipBody={tooltipContent ? <TooltipBody>{tooltipContent}</TooltipBody> : undefined}>
          <Checkbox isDisabled={disabledCheckbox} isChecked={isSelectedMember} onChange={onTickMemberLocal} />
        </DivTooltip>
      </TableBodyCell>
    );
  }, [disabledCheckbox, displayCheckboxes, isSelectedMember, onTickMemberLocal]);

  const { gainedResourceCell, isHighlightedAdmittance } = useMemo(() => {
    let highlighted = false;
    let cell: React.ReactNode = null;

    if (!settings.supply.isEnabled || !isMember) {
      return { gainedResourceCell: null, isHighlightedAdmittance: false };
    }

    const accumulativeClanResource = isOwn ? currentAccount.accumulativeClanResource : member.accumulativeClanResource;
    const isBonusActivated = isOwn ? currentAccount.isBonusActivated : member.isBonusActivated;

    const accountRole = currentAccount.roleName;
    const canViewSupplyAdmittance = hasPermission(accountRole, PERMISSIONS.SUPPLY_VIEW_CLAN_ADMITTANCE);
    const canViewLeveling = canViewSupplyAdmittance || isOwn;

    const isVisibleResource = !isHiddenStatistics || canViewLeveling;

    highlighted = !isOwn && canViewSupplyAdmittance && !isBonusActivated;

    cell = (
      <TableBodyCell modify={'gainedResource' as TableBodyCellModifier}>
        <ResourceCell
          memberId={member.id}
          value={accumulativeClanResource}
          isHidden={!isVisibleResource}
          canViewLeveling={canViewLeveling}
          isBonusActivated={isBonusActivated}
          isHighlightedAdmittance={highlighted}
          disableTooltip={isContextMenuActive}
        />
      </TableBodyCell>
    );

    return { gainedResourceCell: cell, isHighlightedAdmittance: highlighted };
  }, [
    currentAccount.accumulativeClanResource,
    currentAccount.isBonusActivated,
    currentAccount.roleName,
    isContextMenuActive,
    isHiddenStatistics,
    isMember,
    isOwn,
    member.accumulativeClanResource,
    member.id,
    member.isBonusActivated,
  ]);

  const contextMenu = isContextMenuEnabled ? (
    <ContextMenu
      context={contextMenuContext}
      onContextMenuStateChange={onContextMenuStateChange}
      onItemClick={onContextMenuItemClick}
      position={contextMenuPosition}
      isDemo={false}
    />
  ) : null;

  const rowOnContextMenu = isContextMenuEnabled ? onContextMenu : undefined;

  return (
    <TableRow
      isHovering
      isActive={isSelectedMember || isContextMenuActive}
      isOwn={isOwn}
      onContextMenu={rowOnContextMenu}
      onClick={onTableRowClick}
    >
      {contextMenu}
      {tableCellWithCheckboxes}

      <TableBodyCell modify="role">
        <ClanRole role={member.roleName} isLarger />
      </TableBodyCell>

      <TableBodyCell modify="basis">
        <ClanMember
          name={member.name}
          hasPermanentBan={member.isBanned}
          isHiddenStatistics={isHiddenStatistics}
          isOwn={isOwn}
          isHighlightedAdmittance={isHighlightedAdmittance}
          isOnline={isOwnClan && typeof member.onlineStatus === 'boolean' ? member.onlineStatus : undefined}
          disableTooltip={isContextMenuActive}
          role={getLocalizedName(member.roleName)}
          memberIsBannedTooltipId={MemberIsBannedTooltip.id}
        />
      </TableBodyCell>

      {gainedResourceCell}

      <TableBodyCell modify="right">
        <StatisticsValue
          value={thousands(member.battlesCount)}
          isHidden={hideStatistics}
          disableTooltip={isContextMenuActive}
        />
      </TableBodyCell>

      <TableBodyCell modify="right">
        <StatisticsValue
          value={percent(member.winsPercentage)}
          isHidden={hideStatistics}
          disableTooltip={isContextMenuActive}
        />
      </TableBodyCell>

      <TableBodyCell modify="right" isHidden={currentBattleType === CLAN_BATTLES_TYPE}>
        <StatisticsValue
          value={thousands(member.expPerBattle)}
          isHidden={hideStatistics}
          disableTooltip={isContextMenuActive}
        />
      </TableBodyCell>

      <TableBodyCell modify="right" isHidden={currentBattleType === CLAN_BATTLES_TYPE}>
        <StatisticsValue
          value={thousands(member.damagePerBattle)}
          isHidden={hideStatistics}
          disableTooltip={isContextMenuActive}
        />
      </TableBodyCell>

      <TableBodyCell modify="right" isHidden={currentBattleType === CLAN_BATTLES_TYPE}>
        <StatisticsValue
          value={floats(member.fragsPerBattle, 2)}
          isHidden={hideStatistics}
          disableTooltip={isContextMenuActive}
        />
      </TableBodyCell>

      <TableBodyCell modify="right">{thousands(member.daysInClan)}</TableBodyCell>

      <TableBodyCell modify="right">
        <DivTooltip tooltipBody={<LastBattleTimeTooltipContent date={member.lastBattleTime} />}>
          <FormattedLastBattleTime
            date={member.lastBattleTime}
            isHidden={hideStatistics}
            disableTooltip={isContextMenuActive}
          />
        </DivTooltip>
      </TableBodyCell>
    </TableRow>
  );
};

export default memo(MembersTableItem);
